import { NurseLayout } from "@/components/layout/nurse-layout";
import {
  useGetScenario, getGetScenarioQueryKey,
  useListConditions, getListConditionsQueryKey,
  useListMedications, getListMedicationsQueryKey,
} from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { useParams, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Lightbulb, BookOpen, ShieldCheck, Pill, Stethoscope, ChevronRight, Award } from "lucide-react";
import { BackButton } from "@/components/ui/back-button";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { UkHealthDisclaimer } from "@/components/ui/uk-health-disclaimer";

export default function NurseScenarioDetail() {
  const { id } = useParams();
  const [showAnswer, setShowAnswer] = useState(false);

  const { data: scenario, isLoading, error } = useGetScenario(Number(id), {
    query: { enabled: !!id, queryKey: getGetScenarioQueryKey(Number(id)) }
  });

  const hasConditions = (scenario?.relatedConditionIds?.length ?? 0) > 0;
  const hasMedications = (scenario?.relatedMedicationIds?.length ?? 0) > 0;
  const hasRelated = hasConditions || hasMedications;

  const { data: allConditions } = useListConditions(
    undefined,
    { query: { enabled: hasConditions, queryKey: getListConditionsQueryKey() } }
  );

  const { data: allMedications } = useListMedications(
    undefined,
    { query: { enabled: hasMedications, queryKey: getListMedicationsQueryKey() } }
  );

  const relatedConditions = hasConditions && allConditions
    ? allConditions.filter(c => scenario!.relatedConditionIds!.includes(c.id))
    : [];

  const relatedMedications = hasMedications && allMedications
    ? allMedications.filter(m => scenario!.relatedMedicationIds!.includes(m.id))
    : [];

  if (isLoading) return <NurseLayout title="Scenario"><LoadingScreen /></NurseLayout>;
  if (error || !scenario) return <NurseLayout title="Scenario"><div>Scenario not found</div></NurseLayout>;

  const typeLabel: Record<string, string> = {
    advice_line: "Advice Line",
    nmc_mock: "NMC Mock",
    ftp_daily_drip: "FTP Daily Drip",
    rcn_competency: "RCN Competency",
  };

  return (
    <NurseLayout title="Clinical Scenario">
      <BackButton href="/nurse/scenarios" className="pl-0 text-slate-500 hover:text-slate-900 -ml-2 mb-6">Back to Scenarios</BackButton>

      <UkHealthDisclaimer variant="nurse" compact />

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6">
        <div className="bg-slate-50 p-6 border-b border-slate-200">
          <div className="flex gap-2 mb-4">
            <Badge variant="outline" className="bg-white">
              {typeLabel[scenario.type] ?? scenario.type.replace(/_/g, " ").toUpperCase()}
            </Badge>
            <Badge variant="secondary" className="bg-white capitalize">{scenario.difficulty}</Badge>
            {scenario.category && (
              <Badge variant="secondary" className="bg-teal-50 text-teal-700">{scenario.category}</Badge>
            )}
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-4">{scenario.title}</h1>

          <div className="prose prose-slate max-w-none">
            <p className="text-slate-800 text-lg leading-relaxed whitespace-pre-wrap">{scenario.description}</p>
          </div>
        </div>

        {!showAnswer ? (
          <div className="p-12 flex flex-col items-center justify-center bg-slate-50/50">
            <Lightbulb className="h-12 w-12 text-amber-400 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Consider your approach</h3>
            <p className="text-slate-500 mb-6 text-center max-w-md">
              Think about how you would handle this situation in clinical practice before revealing the model answer.
            </p>
            <Button onClick={() => setShowAnswer(true)} className="bg-teal-700 hover:bg-teal-800 text-white px-8">
              Reveal Answer & Rationale
            </Button>
          </div>
        ) : (
          <div className="p-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {scenario.answer && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-teal-900 mb-4 flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-teal-600" /> Model Answer
                </h3>
                <div className="p-6 bg-teal-50/50 border border-teal-100 rounded-xl">
                  <p className="text-slate-800 whitespace-pre-wrap leading-relaxed">{scenario.answer}</p>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-8">
              {scenario.keyLearningPoints && scenario.keyLearningPoints.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-amber-500" /> Key Learning Points
                  </h3>
                  <ul className="space-y-3">
                    {scenario.keyLearningPoints.map((point, i) => (
                      <li key={i} className="flex gap-3 text-slate-700">
                        <span className="flex-shrink-0 h-6 w-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">{i + 1}</span>
                        <span className="mt-0.5">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="space-y-6">
                {scenario.nmcCodeRef && (
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-indigo-500" /> NMC Code Reference
                    </h3>
                    <div className="p-6 bg-slate-900 text-white rounded-xl">
                      <p className="text-slate-300 whitespace-pre-wrap">{scenario.nmcCodeRef}</p>
                    </div>
                  </div>
                )}

                {scenario.rcnRef && (
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                      <Award className="h-5 w-5 text-teal-600" /> RCN Competency Framework
                    </h3>
                    <div className="p-6 bg-teal-900 text-white rounded-xl">
                      <p className="text-teal-100 whitespace-pre-wrap">{scenario.rcnRef}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {hasRelated && (
              <div className="mt-8 pt-8 border-t border-slate-100">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Related Clinical Reference</h3>
                <div className="space-y-4">
                  {relatedConditions.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                        <Stethoscope className="h-3.5 w-3.5" /> Conditions
                      </p>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {relatedConditions.map(condition => (
                          <Link key={condition.id} href={`/nurse/conditions/${condition.id}`}>
                            <div className="group flex items-center gap-3 bg-white border border-teal-100 hover:border-teal-400 hover:shadow-sm rounded-xl p-4 transition-all cursor-pointer">
                              <div className="bg-teal-100 rounded-lg p-2 shrink-0 group-hover:bg-teal-200 transition-colors">
                                <Stethoscope className="h-4 w-4 text-teal-700" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-900 text-sm leading-snug line-clamp-2">{condition.name}</p>
                                {condition.conditionType && (
                                  <p className="text-xs text-teal-600 mt-0.5 capitalize">{condition.conditionType.replace(/_/g, " ")}</p>
                                )}
                              </div>
                              <ChevronRight className="h-4 w-4 text-teal-400 shrink-0 group-hover:text-teal-600 transition-colors" />
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {relatedMedications.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                        <Pill className="h-3.5 w-3.5" /> Medications
                      </p>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {relatedMedications.map(medication => (
                          <Link key={medication.id} href={`/nurse/pharmacology/${medication.id}`}>
                            <div className="group flex items-center gap-3 bg-white border border-indigo-100 hover:border-indigo-400 hover:shadow-sm rounded-xl p-4 transition-all cursor-pointer">
                              <div className="bg-indigo-100 rounded-lg p-2 shrink-0 group-hover:bg-indigo-200 transition-colors">
                                <Pill className="h-4 w-4 text-indigo-700" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-900 text-sm leading-snug line-clamp-2">{medication.name}</p>
                                {medication.drugClass && (
                                  <p className="text-xs text-indigo-600 mt-0.5">{medication.drugClass}</p>
                                )}
                              </div>
                              <ChevronRight className="h-4 w-4 text-indigo-400 shrink-0 group-hover:text-indigo-600 transition-colors" />
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </NurseLayout>
  );
}
